// ─────────────────────────────────────────────
//  Sheet room-family domain — per-facility OR-name prefix matcher used to
//  scope Google Sheet imports (parsePrelimAssignments opts.roomFamily) to
//  the selected facility's rooms only (REQ-sheets-sync).
//
//  PROVENANCE — ⚠️ NO LONGER TRANSCRIBED FROM A COMMENT.
//   The campus split now lives as DATA in ../domain/roomCampus.js: one rule
//   per campus, each carrying why it is true and the evidence that proved it,
//   and these regexes are RECOMPOSED from that table. All three campuses are
//   verified against the department's own `Room Defs` tab (2026-08-20), which
//   is why `carrollwood` no longer says `provisional`.
//
//   ⚠️ THE RULE IS COUNTER-INTUITIVE AND HAS CAUSED AN INCIDENT:
//     WC5  → TAMPA (Women's Center)     WC 5 → WESLEY CHAPEL
//   One space. Read roomCampus.js before changing anything here.
//
//   These patterns now also claim BABY (Wesley Chapel) and ICU (Tampa) —
//   staffed anaesthetising locations on the live board tabs that matched NO
//   pattern at all, so every row in them was dropped before the skip list
//   could see it. `campusForRoomLabel` explains each decision; this file only
//   supplies the coarse row filter.
// ─────────────────────────────────────────────

import { roomFamilyPattern } from "./roomCampus";

export const ROOM_FAMILY_BY_FACILITY = {
  default: roomFamilyPattern("default"),
  tampa: { regex: roomFamilyPattern("tampa") },
  carrollwood: { regex: roomFamilyPattern("carrollwood") },
};

// A regex that matches NOTHING — the safe default for a facility we have no
// pattern for. Previously an unknown facility fell back to the Wesley Chapel
// regex, which silently mis-imported a new hospital's rooms as WC rooms
// (blocker #4). Matching nothing yields an empty import ("no rooms matched")
// the operator can SEE and fix, instead of plausible garbage.
export const NEVER_MATCH_RE = /(?!)/;

// Compile a facility-configured room-family pattern into a RegExp. Returns null
// on an empty or invalid pattern so callers fall back to the built-in map. The
// pattern is a plain string stored on the facility doc (facility.roomFamily);
// flags default to case-insensitive.
export function compileRoomFamily(pattern, flags = "i") {
  if (typeof pattern !== "string" || pattern.trim() === "") return null;
  // Respect an explicit "" (case-sensitive); only coerce a null/non-string
  // flags argument to the "i" default.
  const f = typeof flags === "string" ? flags : "i";
  try {
    return new RegExp(pattern, f);
  } catch {
    return null; // malformed pattern OR bad flags — caller falls back, never throws
  }
}

// Read a facility doc's configured room-family pattern, if any.
// facilityDoc.roomFamily = { pattern: string, flags?: string }.
function configuredFamily(facilityDoc) {
  const rf = facilityDoc && facilityDoc.roomFamily;
  if (!rf) return null;
  return compileRoomFamily(rf.pattern, rf.flags);
}

/**
 * Resolve the room-family RegExp for a facility. Resolution order:
 *   1. the facility doc's CONFIGURED pattern (facility.roomFamily.pattern) —
 *      lets a NEW hospital define its own OR-name prefixes without a code change
 *      (blocker #4);
 *   2. the built-in ROOM_FAMILY_BY_FACILITY map (the verified AdventHealth
 *      campuses — unchanged behaviour for default/tampa/carrollwood);
 *   3. NEVER_MATCH_RE — an unknown, unconfigured facility imports nothing
 *      (visible) rather than falling back to Wesley Chapel's rooms (silent
 *      garbage).
 *
 * @param {string} facilityId
 * @param {object} [opts]
 * @param {Object<string,object>} [opts.configByFacility]  — { fid: facilityDoc }
 *   (or { fid: { roomFamily } }); used to look up a configured pattern.
 * @returns {RegExp}
 */
export function roomFamilyForFacility(facilityId, opts = {}) {
  const configByFacility = opts.configByFacility || {};
  const configured = configuredFamily(configByFacility[facilityId]);
  if (configured) return configured;

  const entry = ROOM_FAMILY_BY_FACILITY[facilityId];
  if (entry) return entry instanceof RegExp ? entry : entry.regex;

  return NEVER_MATCH_RE;
}
